import asyncSeries from 'async/series';
import EventEmitter from 'events';
import { join as pathJoin } from 'path';

import DeviceHashStore from '../stores/device_hash_store';
import MediaStore from '../stores/media_store';

import { AsyncIterator } from '../tools/async_iterator';
import HashCache from './hash_cache';
import util from '../tools/util';
import SwiftMultiWeb from 'swift-multi-web';
import S3Helper from '../tools/s3_helper';

export default {
  once,
  addChangeListener,
  removeChangeListener,
  init,
  getSettings,
  saveSettings,
  getSummary,
  ingest,
};

const ERROR_LIMIT = 10;
const EXPIRE_SECS = 14 * 24 * 60 * 60;
const RETRY_INTERVAL = 20 * 1000;
const FILESTORE_AUTH_URL =
  'https://avid-fs-server-prod.monp-dev.com/api/1/filestore_auth/';

let g_inflightCount = 3;
let g_uploadLimit = 5;

let g_itemId = 1;

const g_eventEmitter = new EventEmitter();
const CHANGE_EVENT = 'change';
function once(callback) {
  g_eventEmitter.once(CHANGE_EVENT, callback);
}
function addChangeListener(callback) {
  g_eventEmitter.on(CHANGE_EVENT, callback);
}
function removeChangeListener(callback) {
  g_eventEmitter.removeListener(CHANGE_EVENT, callback);
}
function _emit(...args) {
  g_eventEmitter.emit(CHANGE_EVENT, ...args);
}
function init() {
  g_inflightCount = _getSetting('MONP_INFLIGHT_COUNT', g_inflightCount);
  g_uploadLimit = _getSetting('MONP_UPLOAD_LIMIT', g_uploadLimit);
}
function getSettings() {
  return {
    inflight_count: g_inflightCount,
    upload_limit: g_uploadLimit,
  };
}
function saveSettings(params) {
  g_inflightCount = params.inflight_count;
  g_uploadLimit = params.upload_limit;
  window.localStorage.MONP_INFLIGHT_COUNT = g_inflightCount;
  window.localStorage.MONP_UPLOAD_LIMIT = g_uploadLimit;
}

function getSummary(params) {
  let file_count = 0;
  let total_size = 0;
  const { file_list } = params;

  if (file_list) {
    file_count = file_list.length;
    for (let i = 0; i < file_count; i++) {
      total_size += file_list[i].size;
    }
  }
  return {
    file_count,
    total_size,
  };
}
function ingest(params, done) {
  const { org_id, volume_id, file_list, dest_dir } = params;
  const list = [];
  for (let i = 0; i < file_list.length; i++) {
    const file = file_list[i];
    const local_path = file.webkitRelativePath;
    const media_path = pathJoin(dest_dir, local_path);
    list.push({
      item_id: g_itemId++,
      file,
      volume_id,
      local_path,
      media_path,
      status: 'idle',
      upload_tries: 0,
      progress: 0,
      hash: null,
      fatal_err: null,
      last_err: null,
      skip: false,
      last_updated: Date.now(),
      start_upload_time: 0,
    });
  }
  const summary = getSummary(params);
  _emit('start', summary, list);

  const opts = {
    list,
    limit: g_uploadLimit,
    times: ERROR_LIMIT,
    interval: RETRY_INTERVAL,
    iterator: _uploadFile,
    errorFilter: _errorFilter,
  };
  const iter = new AsyncIterator(opts);
  iter.on('item_final_error', (err, item) => {
    console.log('item_final_error:', err, item);
    _emit('item_fatal_error', item, err);
  });
  iter.on('item_done', (item) => {
    _emit('item_done', item);
  });
  iter.on('item_error', (err, item) => {
    console.log('item_error:', err, item);
    _emit('item_soft_error', item, err);
  });
  iter.on('done', (err) => {
    _emit('done', err);
    done();
  });
  iter.start();
}

function _uploadFile(item, done) {
  const { file, media_path, volume_id } = item;
  const file_size = file.size;
  const mtime = file.lastModified;
  let file_hash: string;
  let media: any;
  let check_result: any;

  item.upload_tries++;
  item.progress = 0;
  item.last_err = null;
  _itemStatus(item, 'start');
  asyncSeries(
    [
      (done) => {
        MediaStore.findMedia({ volume_id, media_path }, (err, found) => {
          if (err) {
            util.errorLog(
              'MediaIngest._uploadFile: find err:',
              err,
              media_path
            );
          } else if (found) {
            media = found;
          }
          done(err);
        });
      },
      (done) => {
        _itemStatus(item, 'hashing');
        HashCache.hashFile(file, (err, hash) => {
          if (err) {
            util.errorLog(
              'MediaIngest._uploadFile: hash err:',
              err,
              media_path
            );
          } else {
            file_hash = hash;
            item.hash = hash;
            if (media && media.file_hash !== file_hash) {
              item.fatal_err = 'hash_mismatch';
              err = 'conflict';
            }
          }
          _itemStatus(item, 'hash_done');
          done(err);
        });
      },
      (done) => {
        if (media) {
          done();
        } else {
          const opts = {
            volume_id,
            media_path,
            file_size,
            file_hash,
            ctime: mtime,
            mtime,
          };
          MediaStore.addMedia(opts, (err, new_media) => {
            if (err) {
              util.errorLog(
                'MediaIngest._uploadFile: add err:',
                err,
                media_path
              );
            }
            media = new_media;
            done(err);
          });
        }
      },
      (done) => {
        const opts = {
          volume_id,
          file_hash,
          file_size,
        };
        DeviceHashStore.checkHash(opts, (err, result) => {
          if (err) {
            util.errorLog(
              'MediaIngest._uploadFile: check err:',
              err,
              media_path,
              file_hash
            );
          }
          check_result = result;
          done(err);
        });
      },
      (done) => {
        const progress = (fraction: number) => {
          item.progress = fraction;
          _emit('item_progress', item);
        };
        const opts = {
          auth: check_result,
          volume_id,
          file,
          file_hash,
          file_size,
          progress,
        };
        item.start_upload_time = Date.now();
        _itemStatus(item, 'uploading');
        if (check_result.have_file) {
          item.skip = true;
          done();
        } else if (check_result.send_filestore) {
          _sendFilestore(opts, done);
        } else if (check_result.send_s3) {
          _sendS3(opts, done);
        } else {
          util.debugLog(
            'MediaIngest._uploadFile: bad check result:',
            check_result
          );
          done('not_implemented');
        }
      },
    ],
    (err?: any) => {
      if (err) {
        item.progress = 0;
      } else {
        item.progress = 1;
      }
      item.last_err = err;
      _itemStatus(item, 'done');
      done(err);
    }
  );
}

function _sendFilestore(params, done) {
  const { volume_id, file, file_hash, auth, progress } = params;
  const filestore_auth = {
    os_auth_url: FILESTORE_AUTH_URL,
    os_username: auth.filestore_username,
    os_password: auth.filestore_password,
    os_tenant_name: auth.filestore_tenant,
  };

  const delete_at = Math.floor(Date.now() / 1000) + EXPIRE_SECS;
  const expire_time = new Date(delete_at * 1000);
  asyncSeries(
    [
      (done) => {
        const opts = {
          ...filestore_auth,
          container: auth.filestore_container,
          file,
          object_path: file_hash,
          delete_at,
          inflight_count: g_inflightCount,
          progress,
          console_log: _consoleLog,
          error_log: _errorLog,
        };
        SwiftMultiWeb.sendFile(opts, done);
      },
      (done) => {
        const opts = {
          volume_id,
          file_hash,
          expire_time,
        };
        DeviceHashStore.addFilestoreObject(opts, (err) => {
          if (err) {
            util.errorLog('MediaIngest._sendFilestore: err:', err, file_hash);
          }
          done(err);
        });
      },
    ],
    done
  );
}
function _sendS3(params, done) {
  const { file, file_hash, auth, progress } = params;
  asyncSeries(
    [
      (done) => {
        const opts = {
          request: auth.s3_upload_req,
          file,
          progress,
        };
        S3Helper.sendFile(opts, done);
      },
      (done) => {
        const opts = {
          file_hash,
          file_size: file.size,
        };
        DeviceHashStore.addS3Object(opts, (err) => {
          if (err) {
            util.errorLog('MediaIngest._sendS3: err:', err, file_hash);
          }
          done(err);
        });
      },
    ],
    done
  );
}

function _errorFilter(err, item) {
  let ret = true;
  if (item.fatal_err) {
    util.errorLog('MediaIngest._errorFilter: stop:', err, item.fatal_err, item);
    ret = false;
  }
  return ret;
}
function _itemStatus(item, status) {
  item.status = status;
  item.last_updated = Date.now();
  _emit('item_update', item);
}
function _consoleLog(...args) {
  util.log('file_upload:', ...args);
}
function _errorLog(...args) {
  util.errorLog('file_upload:', ...args);
}
function _getSetting(name: string, default_value: number): number {
  let ret = default_value;
  const s = window.localStorage[name];
  if (s) {
    const i = parseInt(s);
    if (i > 0) {
      ret = i;
    }
  }
  return ret;
}
